import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import accountingApi from '../services/accountingApi';

function BankAccountsIndex() {
    const [bankAccounts, setBankAccounts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadBankAccounts();
    }, []);

    const loadBankAccounts = async () => {
        try {
            const response = await accountingApi.getBankAccounts();
            if (response.success) {
                const accountsData = Array.isArray(response.data) ? response.data : (response.data?.bank_accounts || response.data?.data || []);
                setBankAccounts(accountsData);
            }
        } catch (error) {
            console.error('Error loading bank accounts:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeactivate = async (id, currentStatus) => {
        try {
            const response = await accountingApi.updateBankAccount(id, { is_active: !currentStatus });
            if (response.success) {
                loadBankAccounts();
            }
        } catch (error) {
            console.error('Error updating bank account:', error);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    if (loading) return <div className="p-4 md:p-8 text-center">Loading...</div>;

    const totalAccounts = bankAccounts.length;
    const activeAccounts = bankAccounts.filter(a => a.is_active !== false).length;
    const totalBalance = bankAccounts.reduce((sum, a) => sum + parseFloat(a.current_balance || a.opening_balance || 0), 0);

    return (
        <div className="max-w-7xl mx-auto px-3 md:px-4 py-4 md:py-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4 md:mb-6">
                <div>
                    <h1 className="text-xl md:text-3xl font-bold text-slate-800">Bank Accounts</h1>
                    <p className="text-sm text-slate-600 mt-0.5">Manage your bank accounts</p>
                </div>
                <div className="flex gap-2">
                    <Link
                        to="/accounting"
                        className="px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-1.5 text-sm"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <span className="hidden sm:inline">Dashboard</span>
                    </Link>
                    <Link
                        to="/accounting/bank-accounts/new"
                        className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1.5 text-sm font-medium"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <span>Add Bank</span>
                    </Link>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-2 md:gap-6 mb-4 md:mb-8">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 md:p-6">
                    <div className="flex items-center gap-2 md:gap-4">
                        <div className="w-8 h-8 md:w-12 md:h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 md:w-6 md:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs md:text-sm text-slate-600 truncate">Total</p>
                            <p className="text-lg md:text-2xl font-bold text-slate-900">{totalAccounts}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 md:p-6">
                    <div className="flex items-center gap-2 md:gap-4">
                        <div className="w-8 h-8 md:w-12 md:h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 md:w-6 md:h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs md:text-sm text-slate-600 truncate">Balance</p>
                            <p className="text-sm md:text-2xl font-bold text-slate-900 truncate">{formatCurrency(totalBalance)}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 md:p-6">
                    <div className="flex items-center gap-2 md:gap-4">
                        <div className="w-8 h-8 md:w-12 md:h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 md:w-6 md:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs md:text-sm text-slate-600 truncate">Active</p>
                            <p className="text-lg md:text-2xl font-bold text-slate-900">{activeAccounts}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Cards View */}
            <div className="block md:hidden space-y-3">
                {bankAccounts.length > 0 ? bankAccounts.map(account => (
                    <div key={account.id} className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                        <div className="flex items-start gap-3 mb-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-slate-900">{account.bank_name}</p>
                                <p className="text-sm text-slate-500 truncate">{account.account_number}</p>
                                <p className="text-xs text-slate-400">{account.branch_name || account.ifsc_code}</p>
                            </div>
                            <span className={`px-2 py-1 text-xs rounded-full flex-shrink-0 ${account.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                {account.is_active !== false ? 'Active' : 'Inactive'}
                            </span>
                        </div>

                        <div className="flex items-center gap-2 mb-3">
                            <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-700">
                                {account.account_type || 'Current'}
                            </span>
                            {account.is_primary && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-100 text-indigo-700">Primary</span>
                            )}
                        </div>

                        <div className="mb-3">
                            <p className="text-lg font-bold text-slate-900">{formatCurrency(account.current_balance || account.opening_balance || 0)}</p>
                            <p className="text-xs text-slate-500">Opening: {formatCurrency(account.opening_balance || 0)}</p>
                        </div>

                        <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100">
                            <Link to={`/accounting/bank-accounts/${account.id}`} className="text-xs font-medium text-indigo-600 px-3 py-1.5 bg-indigo-50 rounded-lg">View</Link>
                            <Link to={`/accounting/bank-accounts/${account.id}/edit`} className="text-xs font-medium text-slate-600 px-3 py-1.5 bg-slate-100 rounded-lg">Edit</Link>
                            {!account.is_primary && (
                                <button onClick={() => handleDeactivate(account.id, account.is_active)} className={`text-xs font-medium px-3 py-1.5 rounded-lg ${account.is_active !== false ? 'text-rose-600 bg-rose-50' : 'text-green-600 bg-green-50'}`}>
                                    {account.is_active !== false ? 'Deactivate' : 'Activate'}
                                </button>
                            )}
                        </div>
                    </div>
                )) : (
                    <div className="bg-white rounded-xl p-8 text-center text-slate-500 border border-slate-200">
                        No bank accounts found. Add one to get started.
                    </div>
                )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                    <h2 className="text-lg font-bold text-slate-800">Bank Accounts</h2>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Bank Details</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Account Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Balance</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {bankAccounts.length > 0 ? bankAccounts.map(account => (
                                <tr key={account.id} className="hover:bg-slate-50">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="font-semibold text-slate-900">{account.bank_name}</p>
                                                <p className="text-sm text-slate-500">{account.account_number}</p>
                                                <p className="text-xs text-slate-400">{account.branch_name || account.ifsc_code}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <span className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-700">{account.account_type || 'Current'}</span>
                                            {account.is_primary && <span className="px-2 py-1 text-xs rounded-full bg-indigo-100 text-indigo-700">Primary</span>}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <p className="font-semibold text-slate-900">{formatCurrency(account.current_balance || account.opening_balance || 0)}</p>
                                        <p className="text-xs text-slate-500">Opening: {formatCurrency(account.opening_balance || 0)}</p>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`px-3 py-1 text-xs rounded-full ${account.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                            {account.is_active !== false ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <Link to={`/accounting/bank-accounts/${account.id}`} className="text-indigo-600 hover:text-indigo-900 text-sm font-medium">View</Link>
                                            <Link to={`/accounting/bank-accounts/${account.id}/edit`} className="text-indigo-600 hover:text-indigo-900 text-sm font-medium">Edit</Link>
                                            {!account.is_primary && (
                                                <button onClick={() => handleDeactivate(account.id, account.is_active)} className={`text-sm font-medium ${account.is_active !== false ? 'text-rose-600' : 'text-green-600'}`}>
                                                    {account.is_active !== false ? 'Deactivate' : 'Activate'}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-slate-500">No bank accounts found. Add one to get started.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default BankAccountsIndex;
